import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Checkbox,
  CircularProgress,
  Alert,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ChatService from '../services/ChatService';

/**
 * ColumnConfigDialog — "Configure table columns" settings for a grid.
 * Lets the user show/hide columns and drag to reorder them; saved per-user,
 * per-entity via UserPreference (TYPE_COLUMN_CONFIG). Visual pattern mirrors
 * OrderableListPanel (drag handle + position), with a checkbox added for
 * visibility.
 */

const SortableColumnRow = ({ id, index, column, checked, onToggle, colors }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1,
        py: 0.5,
        borderBottom: `1px solid ${colors.border}`,
        bgcolor: isDragging ? 'rgba(34,197,94,0.08)' : 'transparent',
        '&:hover': { bgcolor: colors.surfaceElevated },
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          cursor: 'grab',
          display: 'flex',
          alignItems: 'center',
          color: colors.textMuted,
          '&:hover': { color: '#22c55e' },
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <DragIndicatorIcon fontSize="small" />
      </Box>
      <Checkbox
        size="small"
        checked={checked}
        onChange={() => onToggle(column.fieldName)}
        sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' } }}
      />
      <Typography variant="body2" sx={{ color: colors.textPrimary, flex: 1 }}>
        {column.displayLabel || column.fieldName}
      </Typography>
      <Typography variant="caption" sx={{ color: colors.textMuted }}>
        {index + 1}
      </Typography>
    </Box>
  );
};

const ColumnConfigDialog = ({ open, onClose, entityType, colors, onSaved }) => {
  const [columns, setColumns] = useState([]); // [{fieldName, displayLabel}] in current order
  const [visible, setVisible] = useState({}); // fieldName -> boolean
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const load = useCallback(async () => {
    if (!entityType) return;
    setLoading(true);
    setError(null);
    try {
      const [fields, saved] = await Promise.all([
        ChatService.getEntityFields(entityType),
        ChatService.getColumnConfig(entityType),
      ]);

      const tableFields = (fields || []).filter((f) => f.isVisibleInTable);
      const byName = new Map(tableFields.map((f) => [f.fieldName, f]));

      let ordered = [];
      const visMap = {};

      if (saved && saved.length > 0) {
        saved.forEach((s) => {
          const f = byName.get(s.fieldName);
          if (f) {
            ordered.push(f);
            visMap[s.fieldName] = s.visible !== false;
            byName.delete(s.fieldName);
          }
        });
        // Fields added to the entity since the user last configured this grid
        byName.forEach((f) => { ordered.push(f); visMap[f.fieldName] = true; });
      } else {
        ordered = tableFields;
        tableFields.forEach((f) => { visMap[f.fieldName] = true; });
      }

      setColumns(ordered);
      setVisible(visMap);
    } catch (err) {
      console.error('ColumnConfigDialog load failed:', err);
      setError('Could not load columns for this table.');
    } finally {
      setLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleToggle = (fieldName) => {
    setVisible((prev) => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setColumns((prev) => {
      const oldIndex = prev.findIndex((c) => c.fieldName === active.id);
      const newIndex = prev.findIndex((c) => c.fieldName === over.id);
      return arrayMove(prev, oldIndex, newIndex);
    });
  };

  const handleReset = () => {
    const visMap = {};
    columns.forEach((c) => { visMap[c.fieldName] = true; });
    setVisible(visMap);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = columns.map((c, i) => ({
        fieldName: c.fieldName,
        visible: visible[c.fieldName] !== false,
        order: i,
      }));
      const result = await ChatService.saveColumnConfig(entityType, payload);
      if (result?.success === false) {
        setError(result.error || 'Failed to save column config.');
        return;
      }
      onSaved && onSaved(payload);
      onClose();
    } catch (err) {
      setError('Failed to save column config.');
    } finally {
      setSaving(false);
    }
  };

  const visibleCount = columns.filter((c) => visible[c.fieldName] !== false).length;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 0 } }}
    >
      <DialogTitle sx={{ color: colors.textPrimary }}>
        Configure Table Columns
        <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5 }}>
          Check to show a column, drag to reorder. {visibleCount} of {columns.length} shown.
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} sx={{ color: '#22c55e' }} />
          </Box>
        ) : columns.length === 0 ? (
          <Typography variant="body2" sx={{ color: colors.textMuted, px: 2, py: 3 }}>
            No configurable columns for this table.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={columns.map((c) => c.fieldName)} strategy={verticalListSortingStrategy}>
                {columns.map((c, index) => (
                  <SortableColumnRow
                    key={c.fieldName}
                    id={c.fieldName}
                    index={index}
                    column={c}
                    checked={visible[c.fieldName] !== false}
                    onToggle={handleToggle}
                    colors={colors}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
        <Button onClick={handleReset} disabled={loading || saving} sx={{ color: colors.textSecondary, borderRadius: 0, mr: 'auto' }}>
          Show All
        </Button>
        <Button onClick={onClose} disabled={saving} sx={{ color: colors.textSecondary, borderRadius: 0 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving || visibleCount === 0}
          startIcon={saving ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
          sx={{
            bgcolor: '#22c55e', borderRadius: 0,
            '&:hover': { bgcolor: '#16a34a' },
            '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted },
          }}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ColumnConfigDialog;
